import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Search, MessageCircleQuestion, X } from "lucide-react";
import { FAQSchema } from "@/components/seo/StructuredData";

interface FAQItem {
  id: string;
  question_en: string;
  question_fr: string;
  answer_en: string;
  answer_fr: string;
  display_order: number;
}

const FAQ = () => {
  const { t, language } = useLanguage();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const { data, error } = await supabase
          .from("faqs")
          .select("*")
          .eq("is_active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setFaqs(data || []);
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  // Filter FAQs based on search query
  const filteredFaqs = faqs.filter((faq) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const question = language === "fr" ? faq.question_fr : faq.question_en;
    const answer = language === "fr" ? faq.answer_fr : faq.answer_en;
    return (
      question.toLowerCase().includes(query) ||
      answer.toLowerCase().includes(query)
    );
  });

  // Highlight matching text
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-gradient-to-r from-rose-200/60 to-violet-200/60 text-slate-800 rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };
  // Prepare FAQ data for structured data
  const faqSchemaData = faqs.map(faq => ({
    question: language === "fr" ? faq.question_fr : faq.question_en,
    answer: language === "fr" ? faq.answer_fr : faq.answer_en
  }));

  return (
    <section id="faq" className="py-10 lg:py-12 bg-slate-50/50">
      {/* FAQ Schema for SEO */}
      <FAQSchema faqs={faqSchemaData} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-slate-800 flex items-center gap-3">
                <span className="bg-gradient-to-r from-rose-500 to-violet-500 bg-clip-text text-transparent">
                  FAQ
                </span>
                <span className="text-slate-600 text-lg font-normal">
                  {language === "fr" ? "Questions fréquentes" : "Frequently asked"}
                </span>
              </h2>
            </div>
            
            {/* Compact search bar */}
            <div className="relative group md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-rose-500 transition-colors" />
              <input
                type="text"
                placeholder={language === "fr" ? "Rechercher..." : "Search..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/80 border border-slate-200/80 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-400 transition-all duration-300 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-rose-500" />
            </div>
          ) : filteredFaqs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredFaqs.map((faq) => {
                const question = language === "fr" ? faq.question_fr : faq.question_en;
                const answer = language === "fr" ? faq.answer_fr : faq.answer_en;
                const isOpen = openItem === faq.id;

                return (
                  <div
                    key={faq.id}
                    onClick={() => setOpenItem(isOpen ? undefined : faq.id)}
                    className={`group cursor-pointer p-4 rounded-xl border transition-all duration-300 ${
                      isOpen 
                        ? "bg-white border-rose-300 shadow-md shadow-rose-500/10 md:col-span-2" 
                        : "bg-white/70 border-slate-200/80 hover:border-rose-200 hover:bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className={`font-medium text-sm transition-colors ${isOpen ? "text-rose-600" : "text-slate-700 group-hover:text-rose-600"}`}>
                        {highlightText(question, searchQuery)}
                      </h3>
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                        isOpen ? "bg-gradient-to-r from-rose-500 to-violet-500 text-white" : "bg-slate-100 text-slate-400 group-hover:bg-rose-100 group-hover:text-rose-500"
                      }`}>
                        <span className="text-xs font-bold">{isOpen ? "−" : "+"}</span>
                      </div>
                    </div>
                    {isOpen && (
                      <p className="mt-3 text-sm text-slate-500 leading-relaxed animate-fade-in">
                        {highlightText(answer, searchQuery)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 fade-in">
              <MessageCircleQuestion className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {language === "fr"
                  ? "Aucune question ne correspond à votre recherche."
                  : "No questions match your search."}
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 text-rose-500 hover:text-rose-600 text-sm transition-colors"
              >
                {language === "fr" ? "Effacer la recherche" : "Clear search"}
              </button>
            </div>
          )}

          {/* Results count when searching */}
          {searchQuery && filteredFaqs.length > 0 && (
            <p className="text-center text-sm text-slate-400 mt-6">
              {filteredFaqs.length} {language === "fr" ? "résultat" : "result"}
              {filteredFaqs.length !== 1 ? "s" : ""}{" "}
              {language === "fr" ? "trouvé" : "found"}
              {filteredFaqs.length !== 1 && language === "en" ? "s" : ""}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQ;